/**
 * Stepped CoCM billing workflow modal (D25).
 *
 * Rendered through the app ModalManager (see `ModalProvider` →
 * `useBillingWorkflowModal`), like the activity-completion wizard, so it shares
 * the standard modal shell (`@benchmark/ui` Modal: header band, styled close,
 * radius) and centralized open/close management. Self-contained: given a
 * `patientId` + `billingMonth` it resolves its own eligibility, persisted
 * summary, and facade — no per-caller callbacks.
 *
 * Presentation uses the shared `SteppedForm` wizard chrome (progress bar,
 * numbered step labels, standardized navigation). The workflow is
 * action-driven, so results are held in the form values and each step gates on
 * them via `validate`:
 *
 *   1. Review  — verify attested minutes + assessment, then generate the codes
 *   2. Summary — auto-generate the clinical summary when none exists; edit/save
 *   3. Report  — auto-build the report preview; copy it, then submit (freeze +
 *                lock). Instructions say to paste into the EHR before submitting.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMedplum } from "@medplum/react";
import type { Observation, Patient } from "@medplum/fhirtypes";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconCopy,
  IconSparkles,
} from "@tabler/icons-react";
import { FormStep, Modal, SteppedForm } from "@benchmark/ui/components/atomic";
import {
  searchCareTimeByPatient,
  sumBillableCareTimeMinutes,
} from "@benchmark/core/services";
import {
  formatBillingMonthLabel,
  formatCareTimeEntryRow,
  resolveCareTimeActivityDetail,
  getPatientDisplayName,
  getPatientReferenceDisplay,
  serializeBillingReport,
} from "@benchmark/core/resolvers";
import { COCM_ASSESSMENT_FRESHNESS_WEEKS } from "@benchmark/core/constants";
import type {
  BillingCodePacket,
  BillingSummaryReviewFlag,
} from "@benchmark/core/types";
import {
  gatherBillingReportData,
  type BillingReportMissingFields,
} from "@/services/billing-report.gather";
import { useCareTimeSummary } from "@/hooks/domain/useCareTimeSummary";
import { useCoCMBillingSummary } from "@/hooks/domain/useCoCMBillingSummary";
import { useGenerateBillingSummary } from "@/hooks/api/useGenerateBillingSummary";
import { useGenerateBillingCodes } from "@/hooks/api/useGenerateBillingCodes";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { useCurrentUserOptional } from "@/app/auth/CurrentUserContext";
import { createCoCMBillingFacade } from "@/facades/cocm-billing.facade";
import { BillableToggle } from "./BillableToggle";
import { notifyError } from "./MonthlyBillingCard";

/** Props for the stepped billing modal. @agentic connections */
export type BillingModalProps = {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  /** Billing month as `YYYY-MM`. */
  billingMonth: string;
};

/** Inclusive UTC month bounds for a `YYYY-MM` key. */
const monthBounds = (key: string): { start: string; end: string } => {
  const [year, month] = key.split("-").map(Number);
  return {
    start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
    end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString(),
  };
};

/** Workflow state held in the form values so steps can gate via `validate`. */
type BillingWorkflowValues = {
  summary: string;
  summarySaved: boolean;
  packet: BillingCodePacket | null;
  copied: boolean;
};

/** Resolved data the inner wizard needs once loading completes. */
type BillingWorkflowStepsProps = {
  patientId: string;
  billingMonth: string;
  patient: Patient | undefined;
  initialSummary: string;
  attestedMinutes: number;
  hasEligibleAssessment: boolean;
  /** Concern 6: `false` means no completed intake → not billable this month. */
  hasCompletedIntake?: boolean;
  enrollmentStart?: string;
  cocmCarePlanId?: string;
  onClose: () => void;
};

/**
 * Auto-triggers the summarization service on mount when the month has no
 * summary yet (D25 step 2). Mount = entering the Summary step (SteppedForm
 * remounts step content on navigation).
 */
const useAutoRunOnMount = (shouldRun: boolean, run: () => void) => {
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current || !shouldRun) return;
    ranRef.current = true;
    run();
    // Runs at most once per mount; `shouldRun` is captured on entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * A summary the model (or the handler) flagged for human review. `low` reflects
 * the model's own low-confidence self-report; `flags` are the specific items to
 * double-check. Either alone gates the Save action.
 */
type ReviewNotice = {
  low: boolean;
  flags: BillingSummaryReviewFlag[];
};

/**
 * Coerce one raw review-flag entry into `{ label, detail }`, tolerating a
 * legacy flat string (older API shape) so a rolling / independent api↔connections
 * deploy can't render `undefined:` in the warning banner. Returns `null` for an
 * entry that carries no text (dropped by the caller) so the UI never shows an
 * empty "— :" bullet. A string (or an object with no label) becomes a
 * label-less, detail-only bullet.
 */
const normalizeReviewFlag = (
  flag: unknown,
): BillingSummaryReviewFlag | null => {
  if (typeof flag === "string") {
    const detail = flag.trim();
    return detail ? { label: "", detail } : null;
  }
  if (flag && typeof flag === "object") {
    const raw = flag as Partial<BillingSummaryReviewFlag>;
    const label = typeof raw.label === "string" ? raw.label.trim() : "";
    const detail = typeof raw.detail === "string" ? raw.detail.trim() : "";
    if (!label && !detail) return null;
    return { label, detail };
  }
  return null;
};

const SummaryStepBody = ({
  form,
  generating,
  saving,
  reviewNotice,
  autoGenerate,
  onGenerate,
  onSave,
  onChange,
}: {
  form: ReturnType<typeof useForm<BillingWorkflowValues>>;
  generating: boolean;
  saving: boolean;
  reviewNotice: ReviewNotice | null;
  autoGenerate: boolean;
  onGenerate: () => void;
  onSave: () => void;
  onChange: (value: string) => void;
}) => {
  useAutoRunOnMount(autoGenerate, onGenerate);
  return (
    <Stack gap="sm">
      <Textarea
        label="Clinical summary"
        value={form.values.summary}
        onChange={(e) => onChange(e.currentTarget.value)}
        autosize
        minRows={4}
        maxRows={12}
        placeholder="Assessment & plan for the month…"
        disabled={generating}
      />
      {generating ? (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Generating summary…
          </Text>
        </Group>
      ) : null}
      {reviewNotice ? (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
        >
          <Text size="sm" fw={600}>
            {reviewNotice.low
              ? "Low confidence, please double check:"
              : "Please double check:"}
          </Text>
          <Stack gap={4} mt={6}>
            {reviewNotice.flags.length > 0 ? (
              reviewNotice.flags.map((flag, index) => (
                <Text size="sm" key={`${flag.label}-${index}`}>
                  {flag.label ? (
                    <>
                      — <Text span fw={600} inherit>{`${flag.label}:`}</Text>{" "}
                      {flag.detail}
                    </>
                  ) : (
                    `— ${flag.detail}`
                  )}
                </Text>
              ))
            ) : (
              <Text size="sm">— Verify the summary against the record.</Text>
            )}
          </Stack>
        </Alert>
      ) : null}
      {typeof form.errors.summary === "string" ? (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
        >
          {form.errors.summary}
        </Alert>
      ) : null}
      <Group justify="space-between">
        <Button
          variant="light"
          color="purple"
          leftSection={
            generating ? <Loader size={12} /> : <IconSparkles size={14} />
          }
          onClick={onGenerate}
          disabled={generating}
        >
          {form.values.summary.trim() ? "Regenerate" : "Generate with AI"}
        </Button>
        {form.values.summarySaved ? (
          <Badge color="teal" variant="light" size="lg">
            Captured
          </Badge>
        ) : (
          <Button
            loading={saving}
            disabled={
              !form.values.summary.trim() || Boolean(reviewNotice) || generating
            }
            onClick={onSave}
          >
            Capture summary
          </Button>
        )}
      </Group>
      {/* Wording pending ClinOps sign-off (B2.1). */}
      <Text size="xs" c="dimmed">
        Capturing saves the summary as of now — a point-in-time snapshot, not a
        running draft.
      </Text>
    </Stack>
  );
};

const missingFieldWarnings = (
  missingFields: BillingReportMissingFields | null,
): string[] => {
  if (!missingFields) return [];
  const warnings: string[] = [];
  if (missingFields.intake !== "found") {
    warnings.push(
      "Date of Intake is missing — verify it against the record before sending.",
    );
  }
  if (missingFields.enrollment !== "found") {
    warnings.push(
      "CoCM Enrollment Date is missing — verify it against the record before sending.",
    );
  }
  return warnings;
};

const ReportStepBody = ({
  reportText,
  missingFields,
  building,
  copied,
  hasPacket,
  autoBuild,
  onBuild,
  onCopy,
}: {
  reportText: string | null;
  missingFields: BillingReportMissingFields | null;
  building: boolean;
  copied: boolean;
  hasPacket: boolean;
  autoBuild: boolean;
  onBuild: () => void;
  onCopy: () => void;
}) => {
  useAutoRunOnMount(autoBuild, onBuild);
  const warnings = missingFieldWarnings(missingFields);
  return (
    <Stack gap="sm">
      <Text size="sm">
        Review the report below, copy it, and paste it into the external EHR.
        Once pasted, submit to freeze the packet and lock the month.
      </Text>
      {warnings.length > 0 ? (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={16} />}
        >
          <Text size="sm" fw={600}>
            Verify before sending:
          </Text>
          <Stack gap={2} mt={4}>
            {warnings.map((warning) => (
              <Text size="sm" key={warning}>
                — {warning}
              </Text>
            ))}
          </Stack>
        </Alert>
      ) : null}
      {building ? (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Building report preview…
          </Text>
        </Group>
      ) : reportText ? (
        <Card withBorder padding="sm">
          <ScrollArea.Autosize mah={320}>
            <Text
              size="sm"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {reportText}
            </Text>
          </ScrollArea.Autosize>
        </Card>
      ) : null}
      <Group>
        <Button
          variant="light"
          leftSection={<IconCopy size={14} />}
          loading={building}
          disabled={!hasPacket || !reportText}
          onClick={onCopy}
        >
          {copied ? "Copied — copy again" : "Copy report"}
        </Button>
      </Group>
      {copied ? null : (
        <Text size="xs" c="orange">
          Copy the report to enable submission.
        </Text>
      )}
    </Stack>
  );
};

/** Compact activity-log table for the expandable minute log. */
const MinuteLog = ({
  entries,
  loading,
  onEntryToggled,
}: {
  entries: Observation[] | null;
  loading: boolean;
  /** Called with the persisted observation after a billable toggle. */
  onEntryToggled: (updated: Observation) => void;
}) => {
  if (loading) {
    return (
      <Group gap="xs" py="xs" justify="center">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading minute log…
        </Text>
      </Group>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xs">
        No time logged this month.
      </Text>
    );
  }
  return (
    <Table verticalSpacing="xs" fz="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Date</Table.Th>
          <Table.Th>Activity</Table.Th>
          <Table.Th style={{ textAlign: "right" }}>Time</Table.Th>
          <Table.Th style={{ textAlign: "right" }}>Billable?</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entries.map((obs, index) => {
          const row = formatCareTimeEntryRow(obs);
          const detail = resolveCareTimeActivityDetail(obs);
          const context = detail.contextLabels.join(" · ");
          const isActive =
            Boolean(obs.id) &&
            obs.status !== "entered-in-error" &&
            obs.status !== "cancelled";
          return (
            <Table.Tr key={obs.id ?? index}>
              <Table.Td>{row.when}</Table.Td>
              <Table.Td>
                <Stack gap={0}>
                  <Text size="sm">{detail.methodLabel}</Text>
                  {context ? (
                    <Text size="xs" c="dimmed">
                      {context}
                    </Text>
                  ) : null}
                  {detail.note ? (
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {detail.note}
                    </Text>
                  ) : null}
                </Stack>
              </Table.Td>
              <Table.Td
                style={{ textAlign: "right" }}
                c={row.nonBillable ? "dimmed" : undefined}
              >
                {row.minutes}
              </Table.Td>
              <Table.Td style={{ textAlign: "right" }}>
                {isActive ? (
                  <Group justify="flex-end" gap={0}>
                    <BillableToggle
                      observation={obs}
                      onToggled={onEntryToggled}
                      showCaption={false}
                      ariaLabel={`Toggle billable — ${row.activity}, ${row.when}, ${row.minutes} min`}
                    />
                  </Group>
                ) : (
                  <Text size="xs" c="dimmed">
                    —
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

/**
 * Step 1 (Review) body: a centered card with the attested-minute headline, an
 * expandable exact minute log, the assessment chip, and a full-width action to
 * generate the validated billing codes.
 */
const ReviewStepBody = ({
  attestedMinutes,
  hasEligibleAssessment,
  hasCompletedIntake,
  packet,
  generatingCodes,
  loadEntries,
  onGenerateCodes,
  onEntriesMutated,
  error,
}: {
  attestedMinutes: number;
  hasEligibleAssessment: boolean;
  /** Concern 6: `false` renders an "intake required" reason (not billable). */
  hasCompletedIntake?: boolean;
  packet: BillingCodePacket | null;
  generatingCodes: boolean;
  loadEntries: () => Promise<Observation[]>;
  onGenerateCodes: () => void;
  /** Notifies the wizard that a billable toggle changed the attested minutes. */
  onEntriesMutated: () => void;
  /** Step-gate message shown when the user tries to advance without codes. */
  error?: string;
}) => {
  const [logOpen, setLogOpen] = useState(false);
  const [entries, setEntries] = useState<Observation[] | null>(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  // True only after a successful load. A failed load leaves this false so the
  // headline keeps trusting the eligibility prop instead of collapsing to 0
  // from an empty fallback list.
  const [entriesLoaded, setEntriesLoaded] = useState(false);

  const toggleLog = useCallback(() => {
    setLogOpen((open) => {
      const next = !open;
      if (next && entries === null && !entriesLoading) {
        setEntriesLoading(true);
        void loadEntries()
          .then((loaded) => {
            setEntries(loaded);
            setEntriesLoaded(true);
          })
          .catch(() => setEntries([]))
          .finally(() => setEntriesLoading(false));
      }
      return next;
    });
  }, [entries, entriesLoading, loadEntries]);

  // Optimistically reflect a billable toggle in the loaded minute log so the
  // row and the derived attested headline update immediately, ahead of the
  // event-driven eligibility refetch that keeps other views in sync. Also tell
  // the wizard so it can invalidate any already-generated codes — attested
  // minutes just changed, so a stale packet must not be submittable.
  const handleEntryToggled = useCallback(
    (updated: Observation) => {
      setEntries((current) =>
        current
          ? current.map((obs) => (obs.id === updated.id ? updated : obs))
          : current,
      );
      onEntriesMutated();
    },
    [onEntriesMutated],
  );

  // Once the minute log has loaded successfully, derive the headline from it so
  // a toggle is reflected instantly (identical definition to the eligibility
  // figure: billable, non-deleted minutes in the month). Fall back to the
  // eligibility prop before the log is opened or if its load failed.
  const displayedMinutes =
    entriesLoaded && entries !== null
      ? sumBillableCareTimeMinutes(entries)
      : attestedMinutes;

  return (
    <Stack>
      <Card withBorder radius="md" p="lg" w="100%">
        <Stack gap="md" align="center">
          <Text ta="center" size="sm" c="dimmed">
            Confirm this month&apos;s attested time and assessment, then
            generate the validated billing codes.
          </Text>

          <Stack gap={0} align="center">
            <Text fw={800} fz={52} lh={1.1}>
              {displayedMinutes}
            </Text>
            <Text size="sm" c="dimmed">
              attested minutes
            </Text>
          </Stack>

          {hasCompletedIntake === false && (
            <Badge
              color="red"
              variant="light"
              size="lg"
              leftSection={<IconAlertTriangle size={14} />}
            >
              Intake required — not billable
            </Badge>
          )}

          {hasEligibleAssessment ? (
            <Badge
              color="teal"
              variant="light"
              size="lg"
              leftSection={<IconCircleCheck size={14} />}
            >
              Assessment confirmed
            </Badge>
          ) : (
            <Badge
              color="orange"
              variant="light"
              size="lg"
              leftSection={<IconAlertTriangle size={14} />}
            >
              No assessment in {COCM_ASSESSMENT_FRESHNESS_WEEKS} weeks
            </Badge>
          )}

          <Stack gap={4} w="100%">
            <UnstyledButton onClick={toggleLog}>
              <Group gap={6} justify="center">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-hidden
                  component="div"
                >
                  {logOpen ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )}
                </ActionIcon>
                <Text size="sm" c="dimmed">
                  {logOpen ? "Hide minute log" : "View minute log"}
                </Text>
              </Group>
            </UnstyledButton>
            <Collapse in={logOpen}>
              <ScrollArea.Autosize mah={220}>
                <MinuteLog
                  entries={entries}
                  loading={entriesLoading}
                  onEntryToggled={handleEntryToggled}
                />
              </ScrollArea.Autosize>
            </Collapse>
          </Stack>

          {packet ? (
            <Alert
              color="teal"
              variant="light"
              w="100%"
              title="Codes generated"
            >
              <Group gap="sm">
                <Badge color="teal" size="lg">
                  {packet.packageDisplay || "No bill"}
                </Badge>
                <Text size="sm">
                  {packet.careUnits} care units · {packet.billableMinutes}{" "}
                  billable min
                </Text>
              </Group>
            </Alert>
          ) : null}

          {error ? (
            <Alert
              color="red"
              variant="light"
              w="100%"
              icon={<IconAlertTriangle size={16} />}
            >
              {error}
            </Alert>
          ) : null}

          <Button
            fullWidth
            loading={generatingCodes}
            disabled={generatingCodes}
            onClick={onGenerateCodes}
          >
            {packet ? "Regenerate billing codes" : "Generate billing codes"}
          </Button>
        </Stack>
      </Card>
    </Stack>
  );
};

/** Inner wizard — owns the form + SteppedForm + actions once data is loaded. */
const BillingWorkflowSteps = ({
  patientId,
  billingMonth,
  patient,
  initialSummary,
  attestedMinutes,
  hasEligibleAssessment,
  hasCompletedIntake,
  enrollmentStart,
  cocmCarePlanId,
  onClose,
}: BillingWorkflowStepsProps) => {
  const medplum = useMedplum();
  const facade = useMemo(() => createCoCMBillingFacade(medplum), [medplum]);
  const { generateSummary } = useGenerateBillingSummary();
  const { generateCodes } = useGenerateBillingCodes();
  const performerRoleId = useCurrentUserOptional()?.primaryPractitionerRole?.id;

  const form = useForm<BillingWorkflowValues>({
    initialValues: {
      summary: initialSummary,
      summarySaved: Boolean(initialSummary.trim()),
      packet: null,
      copied: false,
    },
  });

  // One-shot late prefill when persisted text lands after mount; never re-apply
  // after the user clears or edits the field.
  const persistedPrefillAppliedRef = useRef(Boolean(initialSummary.trim()));

  // Read the form through a ref so the prefill effect depends only on
  // `initialSummary` — never the unstable Mantine `form` object. Listing `form`
  // in the deps re-runs this on every keystroke, the same anti-pattern behind
  // the #1320 infinite-render root cause; the ref keeps a live handle without a
  // reactive dependency. The ref is synced in an effect (never written during
  // render) and declared before its consumer so the sync commits first.
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  });

  useEffect(() => {
    if (!initialSummary.trim() || persistedPrefillAppliedRef.current) {
      return;
    }
    const currentForm = formRef.current;
    if (currentForm.values.summary.trim() || currentForm.values.summarySaved) {
      persistedPrefillAppliedRef.current = true;
      return;
    }
    persistedPrefillAppliedRef.current = true;
    currentForm.setValues({
      summary: initialSummary,
      summarySaved: true,
    });
  }, [initialSummary]);

  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [buildingReport, setBuildingReport] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewNotice, setReviewNotice] = useState<ReviewNotice | null>(null);
  const [reportText, setReportText] = useState<string | null>(null);
  const [reportMissingFields, setReportMissingFields] =
    useState<BillingReportMissingFields | null>(null);

  const packet = form.values.packet;

  const loadEntries = useCallback(async (): Promise<Observation[]> => {
    const { start, end } = monthBounds(billingMonth);
    return searchCareTimeByPatient(medplum, patientId, {
      startDate: start,
      endDate: end,
      count: 1000,
    });
  }, [medplum, patientId, billingMonth]);

  const fetchAttested = useCallback(async () => {
    const entries = await loadEntries();
    return {
      // Attested minutes net out entries flagged non-billable (D-2): the
      // attested figure must reflect what is actually billed. The contributing
      // observation ids still include every final entry for audit lineage.
      attestedMinutes: sumBillableCareTimeMinutes(entries),
      contributingObservationIds: entries
        .map((o) => o.id)
        .filter((id): id is string => Boolean(id)),
    };
  }, [loadEntries]);

  const handleSummaryChange = useCallback(
    (value: string) => {
      // Editing the summary invalidates the copied report (which embeds it), but
      // NOT the generated codes — codes depend on minutes/assessment, and are
      // generated in the prior step, so they survive summary edits.
      form.setValues({ summary: value, summarySaved: false, copied: false });
      if (reviewNotice) setReviewNotice(null);
      setReportText(null);
      setReportMissingFields(null);
    },
    [form, reviewNotice],
  );

  const handleGenerateSummary = useCallback(async () => {
    if (generatingSummary) return;
    setGeneratingSummary(true);
    try {
      const result = await generateSummary(patientId, billingMonth);
      if (result) {
        // Keep any generated codes (independent of the summary); only the
        // report preview is invalidated by a new summary.
        form.setValues({
          summary: result.summary,
          summarySaved: false,
          copied: false,
        });
        setReportText(null);
        setReportMissingFields(null);
        // Tolerate both the structured `{ label, detail }` shape and a legacy
        // flat-string entry so a version-skewed API response still renders
        // cleanly; drop entries with no text.
        const rawFlags: unknown[] = Array.isArray(result.reviewFlags)
          ? result.reviewFlags
          : [];
        const flags = rawFlags
          .map(normalizeReviewFlag)
          .filter((f): f is BillingSummaryReviewFlag => f !== null);
        const low = result.confidence === "low";
        setReviewNotice(low || flags.length > 0 ? { low, flags } : null);
      }
    } catch (err) {
      // Surface generation failures instead of silently resetting the spinner.
      notifyError("Could not generate summary", err);
    } finally {
      setGeneratingSummary(false);
    }
  }, [billingMonth, form, generateSummary, generatingSummary, patientId]);

  const handleSaveSummary = useCallback(async () => {
    // Fail closed, as submitting does: capturing the month creates the billing
    // record, and one written without the saving care manager's role is
    // unattributable for the rest of its life.
    if (!performerRoleId) {
      notifications.show({
        title: "Cannot save summary",
        message:
          "Your practitioner role hasn't loaded yet. Refresh and try again.",
        color: "yellow",
      });
      return;
    }
    setSavingSummary(true);
    try {
      const attested = await fetchAttested();
      await facade.upsertSummary({
        patientId,
        billingMonth,
        patientDisplay: patient
          ? getPatientReferenceDisplay(patient)
          : undefined,
        // Attribute the preliminary record to whoever saved it, so the month
        // has a performer from its first save rather than only once submitted.
        performerRoleId,
        clinicalSummary: form.values.summary,
        ...attested,
      });
      form.setFieldValue("summarySaved", true);
      // Resolve the step gate: the "save the summary to continue" message clears
      // once a summary is saved.
      form.clearFieldError("summary");
    } catch (err) {
      notifyError("Could not save summary", err);
    } finally {
      setSavingSummary(false);
    }
  }, [
    billingMonth,
    facade,
    fetchAttested,
    form,
    patient,
    patientId,
    performerRoleId,
  ]);

  const handleGenerateCodes = useCallback(async () => {
    setGeneratingCodes(true);
    try {
      const result = await generateCodes(patientId, billingMonth);
      if (result) {
        form.setValues({ packet: result, copied: false });
        // Resolve the step gate: the "generate the codes to continue" message
        // clears once codes exist.
        form.clearFieldError("packet");
        setReportText(null);
        setReportMissingFields(null);
      } else {
        notifyError("Could not generate codes", new Error("Please try again."));
      }
    } finally {
      setGeneratingCodes(false);
    }
  }, [billingMonth, form, generateCodes, patientId]);

  // A billable toggle in the review minute log changes attested minutes, so any
  // codes/report already generated from the prior split are stale. Invalidate
  // them (and the copied gate) so the wizard forces a regenerate before the
  // packet can be submitted — a frozen packet must never disagree with the
  // attested minutes it was generated from.
  const handleCareTimeMutated = useCallback(() => {
    if (form.values.packet || form.values.copied) {
      form.setValues({ packet: null, copied: false });
    }
    setReportText(null);
    setReportMissingFields(null);
  }, [form]);

  const buildReport = useCallback(async (): Promise<{
    text: string;
    missingFields: BillingReportMissingFields;
  } | null> => {
    if (!packet || !patient) return null;
    const entries = await loadEntries();
    const { data, missingFields } = await gatherBillingReportData(
      medplum,
      patient,
      billingMonth,
      {
        billableMinutes: packet.billableMinutes,
        billingCode: packet.packageDisplay,
        enrollmentDate: packet.enrollmentWindow?.start ?? enrollmentStart,
        cocmCarePlanId: packet.enrollmentWindow?.carePlanId ?? cocmCarePlanId,
      },
      form.values.summary,
      { careTimeEntries: entries },
    );
    return { text: serializeBillingReport(data), missingFields };
  }, [
    billingMonth,
    cocmCarePlanId,
    enrollmentStart,
    form.values.summary,
    loadEntries,
    medplum,
    packet,
    patient,
  ]);

  const handleAutoBuildReport = useCallback(() => {
    if (reportText || !packet) return;
    setBuildingReport(true);
    void buildReport()
      .then((result) => {
        if (result) {
          setReportText(result.text);
          setReportMissingFields(result.missingFields);
        }
      })
      .catch((err) => notifyError("Could not build report", err))
      .finally(() => setBuildingReport(false));
  }, [buildReport, packet, reportText]);

  const handleCopy = useCallback(async () => {
    try {
      let text = reportText;
      if (!text) {
        const built = await buildReport();
        if (!built) return;
        text = built.text;
        setReportText(built.text);
        setReportMissingFields(built.missingFields);
      }
      await navigator.clipboard.writeText(text);
      form.setFieldValue("copied", true);
      form.clearFieldError("copied");
      notifications.show({
        title: "Billing report copied",
        message: formatBillingMonthLabel(billingMonth),
        color: "green",
      });
    } catch (err) {
      notifyError("Could not copy report", err);
    }
  }, [billingMonth, buildReport, form, reportText]);

  const handleSubmit = useCallback(
    async (values: BillingWorkflowValues) => {
      if (!values.packet) return;
      // Fail closed: submission must be attributed (the packet Composition's
      // author/attester is the submitting role). Don't attempt an unattributed
      // submit — the core action also rejects it.
      if (!performerRoleId) {
        notifications.show({
          title: "Cannot submit billing",
          message:
            "Your practitioner role hasn't loaded yet. Refresh and try again.",
          color: "yellow",
        });
        return;
      }
      setSubmitting(true);
      try {
        const attested = await fetchAttested();
        await facade.submitPacket({
          patientId,
          billingMonth,
          packet: values.packet,
          performerRoleId,
          patientDisplay: patient
            ? getPatientReferenceDisplay(patient)
            : undefined,
          clinicalSummary: values.summary,
          attestedMinutes: attested.attestedMinutes,
          contributingObservationIds: attested.contributingObservationIds,
        });
        notifications.show({
          title: "Billing submitted",
          message: formatBillingMonthLabel(billingMonth),
          color: "green",
        });
        onClose();
      } catch (err) {
        notifyError("Could not submit billing", err);
      } finally {
        setSubmitting(false);
      }
    },
    [
      billingMonth,
      facade,
      fetchAttested,
      onClose,
      patient,
      patientId,
      performerRoleId,
    ],
  );

  // Auto-summarize on entering step 2 only when the month has no summary yet.
  const shouldAutoGenerateSummary =
    !initialSummary.trim() &&
    !form.values.summary.trim() &&
    !form.values.summarySaved;

  const steps = [
    {
      id: "review",
      title: "Review",
      subtitle: "Verify & generate codes",
      validate: (v: BillingWorkflowValues) =>
        v.packet !== null
          ? null
          : { packet: "Generate the billing codes to continue." },
    },
    {
      id: "summary",
      title: "Summary",
      subtitle: "Generate & save",
      validate: (v: BillingWorkflowValues) =>
        v.summary.trim() && v.summarySaved
          ? null
          : { summary: "Save the clinical summary to continue." },
    },
    {
      id: "report",
      title: "Report",
      subtitle: "Copy & submit",
      validate: (v: BillingWorkflowValues) =>
        v.copied ? null : { copied: "Copy the report to submit." },
    },
  ];

  return (
    <SteppedForm<BillingWorkflowValues>
      form={form}
      steps={steps}
      onSubmit={handleSubmit}
      isSubmitting={submitting}
      submitText="Mark verified & submitted"
      onCancel={onClose}
      allowFreeNavigation={false}
    >
      <FormStep id="review">
        <ReviewStepBody
          attestedMinutes={attestedMinutes}
          hasEligibleAssessment={hasEligibleAssessment}
          hasCompletedIntake={hasCompletedIntake}
          packet={packet}
          generatingCodes={generatingCodes}
          loadEntries={loadEntries}
          onGenerateCodes={handleGenerateCodes}
          onEntriesMutated={handleCareTimeMutated}
          error={
            typeof form.errors.packet === "string"
              ? form.errors.packet
              : undefined
          }
        />
      </FormStep>

      <FormStep id="summary">
        <SummaryStepBody
          form={form}
          generating={generatingSummary}
          saving={savingSummary}
          reviewNotice={reviewNotice}
          autoGenerate={shouldAutoGenerateSummary}
          onGenerate={handleGenerateSummary}
          onSave={handleSaveSummary}
          onChange={handleSummaryChange}
        />
      </FormStep>

      <FormStep id="report">
        <ReportStepBody
          reportText={reportText}
          missingFields={reportMissingFields}
          building={buildingReport}
          copied={form.values.copied}
          hasPacket={packet !== null}
          autoBuild={packet !== null && reportText === null}
          onBuild={handleAutoBuildReport}
          onCopy={handleCopy}
        />
      </FormStep>
    </SteppedForm>
  );
};

/**
 * Self-contained billing workflow modal. Resolves the month's eligibility +
 * persisted summary, then renders the wizard in the shared modal shell.
 * @agentic connections
 */
export const BillingModal = ({
  opened,
  onClose,
  patientId,
  billingMonth,
}: BillingModalProps) => {
  const { data: patientResource } = usePatientResource(patientId);
  const patient = useMemo(
    () =>
      patientResource?.resourceType === "Patient" ? patientResource : undefined,
    [patientResource],
  );
  const [year, month] = billingMonth.split("-").map(Number);
  const monthDate = useMemo(
    () => new Date(Date.UTC(year, month - 1, 15)),
    [year, month],
  );

  const { summary: eligibility, loading: eligibilityLoading } =
    useCareTimeSummary(patientId, { month: monthDate, patient });
  const { summary: persisted, loading: persistedLoading } =
    useCoCMBillingSummary(patientId, billingMonth);

  // Both summary hooks keep `loading` stable across event-driven background
  // refetches (see their `loadedKeyRef`), so a billable toggle's
  // `CareTimeUpdated` refresh updates the props live without flipping `loading`
  // back to true — the wizard stays mounted (no packet/summary/step reset)
  // while still gating its initial mount on the first real load.
  const loading = eligibilityLoading || persistedLoading;

  const patientName = patient ? getPatientDisplayName(patient) : undefined;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={2}>
          <Text fw={700} size="lg">
            End of Month Billing
          </Text>
          <Text size="sm" c="dimmed">
            {[patientName, formatBillingMonthLabel(billingMonth)]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </Stack>
      }
      size="lg"
    >
      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text c="dimmed">Loading billing details…</Text>
        </Group>
      ) : (
        <BillingWorkflowSteps
          patientId={patientId}
          billingMonth={billingMonth}
          patient={patient}
          initialSummary={persisted?.clinicalSummary ?? ""}
          attestedMinutes={
            eligibility
              ? eligibility.totalMinutes - (eligibility.nonBillableMinutes ?? 0)
              : 0
          }
          hasEligibleAssessment={eligibility?.hasEligibleAssessment ?? false}
          hasCompletedIntake={eligibility?.hasCompletedIntake}
          enrollmentStart={eligibility?.cocmPeriodStart}
          cocmCarePlanId={eligibility?.cocmCarePlanId}
          onClose={onClose}
        />
      )}
    </Modal>
  );
};
